import React, { useEffect, useMemo, useState } from 'react';
import { addDays, addWeeks, eachDayOfInterval, format, parseISO, startOfWeek } from 'date-fns';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GroupsIcon from '@mui/icons-material/Groups';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import CrewCalendar from '../components/CrewCalendar';
import useProjectsData from '../../hooks/useProjectsData';
import { DEPARTMENTS } from '../../data/projectsData';
import { TODAY, departmentColor, departmentLabel, jobsByDepartment } from '../../utils/projectsUtils';

const ProjectCrewCalendarPage: React.FunctionComponent = () => {
  const { source, loading, error } = useProjectsData();
  const [activeDepts, setActiveDepts] = useState<string[]>(DEPARTMENTS);
  const [horizonStart, setHorizonStart] = useState<Date>(startOfWeek(parseISO(TODAY)));
  const [horizonWeeks, setHorizonWeeks] = useState<number>(3);

  // When live data swaps in, re-sync the department filter to the real departments.
  useEffect(() => {
    setActiveDepts(DEPARTMENTS);
  }, [source]);

  const days = useMemo(
    () => eachDayOfInterval({ start: horizonStart, end: addDays(horizonStart, horizonWeeks * 7 - 1) }),
    [horizonStart, horizonWeeks],
  );

  const toggleDept = (dept: string) =>
    setActiveDepts((prev) => (prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]));

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        mainTitle="Crew Schedules"
        subTitle="Each technician's scheduled days across all project departments — ServMan Tampa"
        titleIcon={<GroupsIcon />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load project data from ServMan. Refresh to try again.
        </Alert>
      )}
      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading project data…
        </Typography>
      )}
      <Alert severity="info" sx={{ mb: 2 }}>
        One row per technician with at least one assignment. Each cell shows how many jobs they’re booked on that day.
      </Alert>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Departments:
          </Typography>
          {DEPARTMENTS.map((dept) => {
            const active = activeDepts.includes(dept);
            return (
              <Chip
                key={dept}
                label={`${departmentLabel(dept)} (${jobsByDepartment(dept).length})`}
                size="small"
                onClick={() => toggleDept(dept)}
                variant={active ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  backgroundColor: active ? departmentColor(dept) : 'transparent',
                  color: active ? 'white' : departmentColor(dept),
                  borderColor: departmentColor(dept),
                }}
              />
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => setHorizonStart(addWeeks(horizonStart, -1))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 170, textAlign: 'center' }}>
            {format(days[0], 'MMM d')} – {format(days[days.length - 1], 'MMM d, yyyy')}
          </Typography>
          <IconButton size="small" onClick={() => setHorizonStart(addWeeks(horizonStart, 1))}>
            <ChevronRightIcon />
          </IconButton>
          <Button size="small" onClick={() => setHorizonStart(startOfWeek(parseISO(TODAY)))}>
            Today
          </Button>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={horizonWeeks}
            onChange={(_event, value) => value && setHorizonWeeks(value)}
          >
            <ToggleButton value={2}>2 wk</ToggleButton>
            <ToggleButton value={3}>3 wk</ToggleButton>
            <ToggleButton value={4}>4 wk</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <CrewCalendar days={days} activeDepts={activeDepts} />
    </Box>
  );
};

export default ProjectCrewCalendarPage;
